import { useEffect, useRef, type CSSProperties } from "react";
import { styled, Typography } from "@schaeffler-commerce/design-system";
import { RouterLink } from "~/common/components/Link";
import { Carousel, type CarouselRefAttributes } from "~/common/components/carousel/Carousel";
import type ComponentTypes from "~/features/cms/componentTypes";
import classNames from "classnames";
import { isSiteRelativeUrl, resolveUrl } from "~/utils/url/urlResolver";

export interface CMSRotatingImagesMediaDTO {
  code?: string;
  url?: string;
  altText?: string;
}

export interface CMSRotatingImagesBannerDTO {
  urlLink?: string;
  external?: boolean;
  name?: string;
  headline?: string;
  content?: string;
  media?: CMSRotatingImagesMediaDTO;
}

export interface CMSRotatingImagesProps {
  type: ComponentTypes.CMSRotatingImages;
  id: string;
  className?: string;
  /**
   * Time in milliseconds between slide rotations.
   */
  timeout?: number;
  /**
   * Cycle plugin effect code coming from the cms2lib
   * {@code RotatingImagesComponentEffect} enum (fade, zoom, turnDown, curtainX).
   * Currently used to drive a CSS opacity transition for the "fade" effect.
   */
  effect?: string;
  banners: CMSRotatingImagesBannerDTO[];
}

export const CMSRotatingImages = ({ banners, timeout = 0, effect, className }: CMSRotatingImagesProps) => {
  const carouselRef = useRef<CarouselRefAttributes>(null);
  const pageAmount = banners.length;

  useEffect(() => {
    if (pageAmount > 1 && timeout > 0) {
      const intervalId = setInterval(() => {
        carouselRef.current?.setNextPage();
      }, timeout);
      return () => clearInterval(intervalId);
    }
  }, [timeout, pageAmount]);

  if (pageAmount === 0) {
    return null;
  }

  return (
    <Carousel
      className={classNames("scha__rotating-images", className)}
      data={banners}
      pageItemsCount={1}
      renderSlide={({ data }) => <RotatingImageSlide banner={data[0]} effect={effect} />}
      ref={carouselRef}
      continuous={true}
    />
  );
};

const RotatingImageSlide = ({ banner, effect }: { banner: CMSRotatingImagesBannerDTO; effect?: string }) => {
  const imageUrl = banner.media?.url;

  if (!imageUrl) {
    return null;
  }

  const altText = banner.media?.altText ?? banner.headline ?? "";
  const fade = effect === "fade";

  const slideStyle: CSSProperties = {
    backgroundImage: `url("${resolveUrl(imageUrl)}")`,
    transitionDuration: "500ms",
  };

  const body = (
    <>
      {(banner.headline || banner.content) && (
        <Overlay>
          {banner.headline && (
            <Typography variant="headlineMedium" component="p">
              {banner.headline}
            </Typography>
          )}
          {banner.content && <Typography variant="bodyMedium">{banner.content}</Typography>}
        </Overlay>
      )}
    </>
  );

  if (!banner.urlLink) {
    return (
      <Slide
        className={classNames("scha__rotating-images__slide", fade && "scha__rotating-images__slide--fade")}
        style={slideStyle}
        role="img"
        aria-label={altText}
      >
        {body}
      </Slide>
    );
  }

  // Site-relative internal slides navigate through the router; external slides
  // (with their target/rel pair) stay raw anchors, HEAD-exact.
  if (!banner.external && isSiteRelativeUrl(banner.urlLink)) {
    return (
      <SlideLink
        className={classNames("scha__rotating-images__slide", fade && "scha__rotating-images__slide--fade")}
        to={banner.urlLink}
        style={slideStyle}
        aria-label={altText}
        data-ga-name={banner.name}
        data-type="rotatingimagescomponent"
      >
        {body}
      </SlideLink>
    );
  }

  return (
    <SlideLink
      as="a"
      className={classNames("scha__rotating-images__slide", fade && "scha__rotating-images__slide--fade")}
      href={resolveUrl(banner.urlLink)}
      target={banner.external ? "_blank" : undefined}
      rel={banner.external ? "noopener noreferrer" : undefined}
      style={slideStyle}
      aria-label={altText}
      data-ga-name={banner.name}
      data-type="rotatingimagescomponent"
    >
      {body}
    </SlideLink>
  );
};

const Slide = styled("div")(({ theme }) => ({
  display: "block",
  width: "100%",
  minHeight: theme.spacing(40),
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
}));

const SlideLink = styled(RouterLink)(({ theme }) => ({
  display: "block",
  width: "100%",
  minHeight: theme.spacing(40),
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
}));

const Overlay = styled("div")(({ theme }) => ({
  maxWidth: "60%",
  padding: theme.spacing(4),
  color: theme.palette.common.white,
  textShadow: `0 ${theme.spacing(0.5)} ${theme.spacing(1)} rgba(0, 0, 0, 0.6)`,
}));
